import { isEnabledLocale } from './localeMatcher'
import { DEFAULT_LOCALE } from './locale'

// All of these check for different shapes of same segment immediately after /region
// valid hyphen locale i.e. en-AU/zh-Hant-TW
const CANONICAL_LOCALE_SEGMENT = /^[a-z]{2}-(?:[A-Z][a-z]{3}-)?[A-Z]{2}$/
// valid underscore we can normalise i.e en_AU -> en-AU
const UNDERSCORE_LOCALE_SEGMENT = /^[a-z]{2}_(?:[A-Z][a-z]{3}_)?[A-Z]{2}$/
// These three match locale-shaped segments that are malformed. We check them explicitly
// to avoid flagging ordinary slugs after the region prefix (e.g. /au/offer).
const MALFORMED_SEPARATOR_LOCALE_SEGMENT = /^[a-z]{2}(?:--|__)[A-Z]{2}$/
const MALFORMED_LANGUAGE_REGION_LOCALE_SEGMENT =
  /^(?:[a-z]{1}|[a-z]{3})[-_][A-Z]{2}$|^[a-z]{2}[-_][A-Z]{3}$/
const MALFORMED_SCRIPT_LOCALE_SEGMENT =
  /^[a-z]{2}[-_][A-Za-z]{3,5}[-_][A-Z]{2}$/

type LocaleUrlResolution =
  | {
    status: 'none'
    pathname: string
    suffix: string
    segments: Array<string>
  }
  | {
    status: 'invalid-locale-shaped'
    regionCode: string
    localeSegment: string
    pathname: string
    suffix: string
    segments: Array<string>
  }
  | {
    status:
      | 'supported-canonical'
      | 'safe-normalization'
      | 'valid-unsupported'
    regionCode: string
    localeSegment: string
    normalizedLocale: string
    pathname: string
    suffix: string
    segments: Array<string>
  }

export type LocaleUrlRedirect = {
  statusCode: 301 | 302
  url: string
}

function pathnameFromRequestUrl(url: string): string {
  const withoutHash = url.split('#')[0] ?? ''
  return (withoutHash.split('?')[0] ?? '').trim()
}

function splitPathnameFromSuffix(url: string): [string, string] {
  const suffixIndex = url.search(/[?#]/)
  return suffixIndex === -1
    ? [url, '']
    : [url.slice(0, suffixIndex), url.slice(suffixIndex)]
}

function normalizeRegionCodes(
  regionCodes: Array<string> | undefined,
): Set<string> | undefined {
  return regionCodes
    ? new Set(regionCodes.map((regionCode) => regionCode.toLowerCase()))
    : undefined
}

function getLocaleUrlResolution(
  url: string,
  supportedRegionCodes?: Array<string>,
  enabledLocales?: Array<string>,
): LocaleUrlResolution {
  const [pathname, suffix] = splitPathnameFromSuffix(url)
  const segments = pathname.split('/')
  const regionCode = segments[1]
  const localeSegment = segments[2]
  const supportedRegions = normalizeRegionCodes(supportedRegionCodes)

  if (
    !pathname.startsWith('/') ||
    !regionCode ||
    !localeSegment ||
    localeSegment.length === 0 ||
    (supportedRegions && !supportedRegions.has(regionCode.toLowerCase()))
  ) {
    return { status: 'none', pathname, suffix, segments }
  }

  if (CANONICAL_LOCALE_SEGMENT.test(localeSegment)) {
    return {
      status: isEnabledLocale(localeSegment, enabledLocales)
        ? 'supported-canonical'
        : 'valid-unsupported',
      regionCode,
      localeSegment,
      normalizedLocale: localeSegment,
      pathname,
      suffix,
      segments,
    }
  }

  if (UNDERSCORE_LOCALE_SEGMENT.test(localeSegment)) {
    const normalizedLocale = localeSegment.replace(/_/g, '-')
    return {
      status: isEnabledLocale(normalizedLocale, enabledLocales)
        ? 'safe-normalization'
        : 'valid-unsupported',
      regionCode,
      localeSegment,
      normalizedLocale,
      pathname,
      suffix,
      segments,
    }
  }

  if (
    MALFORMED_SEPARATOR_LOCALE_SEGMENT.test(localeSegment) ||
    MALFORMED_LANGUAGE_REGION_LOCALE_SEGMENT.test(localeSegment) ||
    MALFORMED_SCRIPT_LOCALE_SEGMENT.test(localeSegment)
  ) {
    return {
      status: 'invalid-locale-shaped',
      regionCode,
      localeSegment,
      pathname,
      suffix,
      segments,
    }
  }

  return { status: 'none', pathname, suffix, segments }
}

/** Replaces `/:region/:locale`, e.g. `/au/en_AU/hotels` -> `/au/en-AU/hotels`. */
function replaceLocaleSegment(
  resolution: LocaleUrlResolution,
  locale: string,
): string {
  return [
    ...resolution.segments.slice(0, 2),
    locale,
    ...resolution.segments.slice(3),
  ].join('/')
}

/** Removes `/:region/:locale`, e.g. `/au/fr-FR/hotels` -> `/au/hotels`. */
function removeLocaleSegment(resolution: LocaleUrlResolution): string {
  return [
    ...resolution.segments.slice(0, 2),
    ...resolution.segments.slice(3),
  ].join('/')
}

function buildRedirect(
  statusCode: 301 | 302,
  currentUrl: string,
  targetUrl: string,
): LocaleUrlRedirect | undefined {
  return currentUrl === targetUrl ? undefined : { statusCode, url: targetUrl }
}

export function replaceLocaleInUrl(url: string, locale: string): string {
  const resolution = getLocaleUrlResolution(url)
  if (locale === DEFAULT_LOCALE) {
    return resolution.status === 'none' ||
      resolution.status === 'invalid-locale-shaped'
      ? url
      : `${removeLocaleSegment(resolution)}${resolution.suffix}`
  }

  if (
    resolution.status !== 'none' &&
    resolution.status !== 'invalid-locale-shaped'
  ) {
    return `${replaceLocaleSegment(resolution, locale)}${resolution.suffix}`
  }

  const [pathname, suffix] = splitPathnameFromSuffix(url)
  const segments = pathname.split('/')
  const regionCode = segments[1]
  if (!pathname.startsWith('/') || !regionCode) {
    return url
  }

  return (
    [...segments.slice(0, 2), locale, ...segments.slice(2)].join('/') + suffix
  )
}

export function getLocaleUrlRedirect(
  url: string,
  supportedRegionCodes?: Array<string>,
  enabledLocales?: Array<string>,
): LocaleUrlRedirect | undefined {
  const resolution = getLocaleUrlResolution(
    url,
    supportedRegionCodes,
    enabledLocales,
  )

  switch (resolution.status) {
    case 'safe-normalization':
      // SEO: /au/en-AU 301s to /au
      if (resolution.normalizedLocale === DEFAULT_LOCALE) {
        return buildRedirect(
          301,
          url,
          `${removeLocaleSegment(resolution)}${resolution.suffix}`,
        )
      }

      return buildRedirect(
        301,
        url,
        `${replaceLocaleSegment(resolution, resolution.normalizedLocale)}${
          resolution.suffix
        }`,
      )
    case 'supported-canonical':
      // SEO: /au/en-AU 301s to /au
      if (resolution.normalizedLocale === DEFAULT_LOCALE) {
        return buildRedirect(
          301,
          url,
          `${removeLocaleSegment(resolution)}${resolution.suffix}`,
        )
      }

      return undefined
    case 'valid-unsupported':
      return buildRedirect(
        302,
        url,
        `${removeLocaleSegment(resolution)}${resolution.suffix}`,
      )
    case 'invalid-locale-shaped':
      return buildRedirect(
        302,
        url,
        `/${resolution.regionCode}${resolution.suffix}`,
      )
    default:
      return undefined
  }
}

export function getLocaleFromPathname(pathname: string): string | null {
  const resolution = getLocaleUrlResolution(pathname)
  if (
    resolution.status === 'none' ||
    resolution.status === 'invalid-locale-shaped'
  ) {
    return null
  }
  return resolution.normalizedLocale
}

export function getLocaleFromRequestUrl(url: string): string | null {
  return getLocaleFromPathname(pathnameFromRequestUrl(url))
}

/**
 * Removes a locale-shaped URL segment from the canonical position immediately
 * after the region, so locale-prefixed URLs still match `/:regionCode/...` routes.
 */
export function removeLocaleFromPathname(pathname: string): string {
  const resolution = getLocaleUrlResolution(pathname)
  if (
    resolution.status !== 'none' &&
    resolution.status !== 'invalid-locale-shaped'
  ) {
    return `${removeLocaleSegment(resolution)}${resolution.suffix}`
  }
  return pathname
}
